// Перенос с Асгарда — миграция одной панели Sputnik Asgard → Shanyrak.
//
// Оператор вводит UUID панели в Sputnik и жмёт «Перенести». Вся
// оркестрация — на стороне GEO backend (мутация migratePanel): fetch
// панели из Sputnik, регистрация в Shanyrak, перенос конфигурации,
// verification-gate, необратимый Sputnik-flip. Здесь — только ввод
// UUID и отображение пошагового результата.
//
// Там же — регистрация камеры Batyr.AI по серийнику агента
// (adminRegisterBatyrCamera): камера заводится в META pool, партнёру её
// передают на «Устройствах».

import { useState } from "react";
import { gql } from "../lib/gql";

// detail — пояснение к прошедшему шагу: что и сколько перенесено.
type Step = { name: string; ok: boolean; error: string | null; detail: string | null };

type MigrateResult = {
  success: boolean;
  deviceUUID: string | null;
  notice: string | null;
  steps: Step[];
};

const MIGRATE = `
  mutation MigratePanel($input: MigratePanelInput!) {
    migratePanel(input: $input) {
      success
      deviceUUID
      notice
      steps { name ok error detail }
    }
  }
`;

const UUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

type BatyrResult = {
  success: boolean;
  notice: string | null;
  deviceId: string | null;
  ownerCode: string | null;
};

const REGISTER_BATYR = `
  mutation RegisterBatyrCamera($serial: String!) {
    adminRegisterBatyrCamera(agentSerial: $serial) {
      success
      notice
      deviceId
      ownerCode
    }
  }
`;

// Серийник агента камеры: EF и 14 шестнадцатеричных цифр.
const AGENT_SERIAL_RE = /^EF[0-9A-F]{14}$/;

export function MigrationPage() {
  const [uuid, setUuid] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MigrateResult | null>(null);

  const uuidValid = UUID_RE.test(uuid.trim());

  async function migrate() {
    if (!uuidValid || busy) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const data = await gql<{ migratePanel: MigrateResult }>(MIGRATE, {
        input: { sputnikUUID: uuid.trim() },
      });
      setResult(data.migratePanel);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Перенос с Асгарда</h2>
      </div>

      <div className="card" style={{ marginBottom: 16, padding: 16 }}>
        <p className="muted" style={{ margin: 0, lineHeight: 1.5 }}>
          Миграция одной панели Sputnik Asgard → Shanyrak. Введите UUID панели в
          Sputnik. Перенос регистрирует устройство в Shanyrak, копирует квартиры,
          ключи и конфигурацию, затем переключает эндпоинты панели на наши хосты.
          Необратимый Sputnik-flip выполняется <b>только</b> если наша сторона
          реально готова (проверка config-gw) — иначе панель остаётся на Sputnik.
          Идемпотентно: повторный запуск безопасен. Панель регистрируется в META
          pool.
        </p>
      </div>

      <div className="card" style={{ marginBottom: 16, padding: 16 }}>
        <div className="form-row">
          <label className="label" htmlFor="sputnik-uuid">
            UUID панели в Sputnik
          </label>
          <input
            id="sputnik-uuid"
            type="text"
            value={uuid}
            onChange={(e) => setUuid(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            spellCheck={false}
            autoComplete="off"
            style={{ width: "100%", fontFamily: "monospace", padding: "8px 10px" }}
            disabled={busy}
          />
        </div>
        {uuid.trim() !== "" && !uuidValid && (
          <p className="muted danger" style={{ margin: "6px 0 0", fontSize: 12 }}>
            Не похоже на UUID.
          </p>
        )}
        <div className="row" style={{ marginTop: 12 }}>
          <button onClick={migrate} disabled={!uuidValid || busy}>
            {busy ? "Переношу…" : "Перенести"}
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {result && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div
            className={result.success ? "badge admin" : "badge inactive"}
            style={{ marginBottom: 12, display: "inline-block" }}
          >
            {result.success ? "Перенос завершён" : "Перенос не завершён"}
          </div>

          {result.notice && (
            <p style={{ margin: "0 0 12px", lineHeight: 1.5 }}>{result.notice}</p>
          )}

          {result.deviceUUID && (
            <p className="muted" style={{ margin: "0 0 12px", fontSize: 12 }}>
              Устройство в Shanyrak:{" "}
              <code>{result.deviceUUID}</code>
            </p>
          )}

          <div>
            {result.steps.map((s, i) => (
              <div
                key={i}
                className="row"
                style={{
                  padding: "6px 0",
                  borderBottom:
                    i < result.steps.length - 1 ? "1px solid #eee" : "none",
                  alignItems: "flex-start",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    color: s.ok ? "#1a7f37" : "#c0392b",
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {s.ok ? "✓" : "✗"}
                </span>
                <span style={{ flex: 1 }}>
                  {s.name}
                  {s.detail && (
                    <span className="muted" style={{ display: "block", fontSize: 12 }}>
                      {s.detail}
                    </span>
                  )}
                  {s.error && (
                    <span
                      className="muted danger"
                      style={{ display: "block", fontSize: 12 }}
                    >
                      {s.error}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>

          {result.success && (
            <p className="muted" style={{ margin: "12px 0 0", lineHeight: 1.5 }}>
              Дёрните <code>update_config</code> на Асгарде (или дождитесь
              переподключения панели) — панель скачает конфиг с{" "}
              <code>cs.shanyrak</code> и переедет на наш WS.
            </p>
          )}
        </div>
      )}

      <BatyrCameraCard />
    </div>
  );
}

// BatyrCameraCard — регистрация камеры Batyr.AI с нашим агентом по
// серийнику. Shanyrak проверяет, что её поток есть в Джемини.
function BatyrCameraCard() {
  const [serial, setSerial] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BatyrResult | null>(null);

  const clean = serial.trim().toUpperCase();
  const valid = AGENT_SERIAL_RE.test(clean);

  async function register() {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const data = await gql<{ adminRegisterBatyrCamera: BatyrResult }>(REGISTER_BATYR, {
        serial: clean,
      });
      setResult(data.adminRegisterBatyrCamera);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between", margin: "8px 0 16px" }}>
        <h2 style={{ margin: 0 }}>Камера Batyr.AI</h2>
      </div>

      <div className="card" style={{ marginBottom: 16, padding: 16 }}>
        <p className="muted" style={{ margin: 0, lineHeight: 1.5 }}>
          Камера с нашим агентом, без панели. Введите серийник агента — EF и 14
          знаков. Shanyrak проверяет, что поток камеры есть в Джемини, и заводит её
          в META pool с архивом на 3 суток. Партнёру камеру передают на странице
          «Устройства» кнопкой «Перенести в другой кабинет», там же её видно в
          списке. Повторная регистрация того же серийника безопасна.
        </p>
      </div>

      <div className="card" style={{ marginBottom: 16, padding: 16 }}>
        <div className="form-row">
          <label className="label" htmlFor="batyr-serial">
            Серийник агента
          </label>
          <input
            id="batyr-serial"
            type="text"
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") register();
            }}
            placeholder="EF00000006114EEA"
            spellCheck={false}
            autoComplete="off"
            style={{ width: "100%", fontFamily: "monospace", padding: "8px 10px" }}
            disabled={busy}
          />
        </div>
        {serial.trim() !== "" && !valid && (
          <p className="muted danger" style={{ margin: "6px 0 0", fontSize: 12 }}>
            Серийник — EF и 14 шестнадцатеричных цифр.
          </p>
        )}
        <div className="row" style={{ marginTop: 12 }}>
          <button onClick={register} disabled={!valid || busy}>
            {busy ? "Регистрирую…" : "Зарегистрировать"}
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {result && (
        <div className="card" style={{ padding: 16 }}>
          <div
            className={result.success ? "badge admin" : "badge inactive"}
            style={{ marginBottom: 12, display: "inline-block" }}
          >
            {result.success ? "Камера зарегистрирована" : "Камера не зарегистрирована"}
          </div>
          {result.notice && (
            <p style={{ margin: "0 0 12px", lineHeight: 1.5 }}>{result.notice}</p>
          )}
          {result.deviceId && (
            <p className="muted" style={{ margin: 0, fontSize: 12 }}>
              Устройство: <code>{result.deviceId}</code>
              {result.ownerCode && (
                <>
                  {" "}
                  · кабинет <code>{result.ownerCode}</code>
                </>
              )}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
